import { AxiosResponse } from "axios";
import HttpService from "./interface/HttpService";
import { SolarProductDetailsState } from "../stores/slices/solar-product-details-slice";
import { SolarProduct } from "../model/solar-product/solar-product";
import { PaginatedList } from "../model/common/paginated-list";
import { NewSolarProduct } from "../model/solar-product/new-solar-product";
import { ExistingSolarProduct } from "../model/solar-product/existing-solar-product";
import { LoanProduct } from "../model/solar-product/loan-product";
import { CategoryWithProduct } from "../model/solar-product/category-with-product";

class SolarProductService {
	private instance: HttpService;
	private controllerName: string = "solar-product";

	constructor() {
		this.instance = new HttpService(this.controllerName);
	}

	public async add(product: SolarProductDetailsState): Promise<AxiosResponse<void>> {
		const form = new FormData();

		form.append("productName", product.firstStep.productName);
		form.append("productCapacity", product.firstStep.productCapacity.toString());
		form.append("price", product.firstStep.price.toString());
        product.firstStep.categoryIds.forEach((id) => {
            form.append("categoryIds", id);
          });
		
		product.firstStep.activeDuration && form.append("activeDuration", product.firstStep.activeDuration);
		product.firstStep.packageDescription && form.append("packageDescription", product.firstStep.packageDescription);
		product.firstStep.batteryType && form.append("batteryType", product.firstStep.batteryType);
		product.secondStep.inverterType && form.append("inverterType", product.secondStep.inverterType);
		product.secondStep.warranty && form.append("warranty", product.secondStep.warranty);
		product.secondStep.batteryVoltage && form.append("batteryVoltage", product.secondStep.batteryVoltage);
		form.append("type", product.secondStep.type);
		product.secondStep.quantityAvailableForSale && form.append("quantityAvailableForSale", product.secondStep.quantityAvailableForSale);
		form.append("installationCostForBungalow", product.thirdStep.costForBungalow.toString());
		form.append("installationCostForFloorForStoryBuilding", product.thirdStep.costPerFloor.toString());
		for await (const image of product.thirdStep.images) {
			let blob = await fetch(image.url).then(r => r.blob());

			let file = new File([blob], image.name, { type: blob.type });

			form.append("images", file, file.name);
		}

		return this.instance.post("", form, { headers: { "Content-Type": "multipart/form-data" } }).catch(error => {
			throw new Error(error.response.data.title);
		});
	}

	public async edit(product: SolarProductDetailsState): Promise<AxiosResponse<void>> {
		const form = new FormData();

		form.append("productName", product.firstStep.productName);
		form.append("productCapacity", product.firstStep.productCapacity.toString());
		form.append("price", product.firstStep.price.toString());
        product.firstStep.categoryIds.forEach((id) => {
            form.append("categoryIds", id);
          });
		product.firstStep.activeDuration && form.append("activeDuration", product.firstStep.activeDuration);
		product.firstStep.packageDescription && form.append("packageDescription", product.firstStep.packageDescription);
		product.firstStep.batteryType && form.append("batteryType", product.firstStep.batteryType);
		product.secondStep.inverterType && form.append("inverterType", product.secondStep.inverterType);
		product.secondStep.warranty && form.append("warranty", product.secondStep.warranty);
		product.secondStep.batteryVoltage && form.append("batteryVoltage", product.secondStep.batteryVoltage);
		form.append("type", product.secondStep.type);
		product.secondStep.quantityAvailableForSale && form.append("quantityAvailableForSale", product.secondStep.quantityAvailableForSale);
		form.append("installationCostForBungalow", product.thirdStep.costForBungalow.toString());
		form.append("installationCostForFloorForStoryBuilding", product.thirdStep.costPerFloor.toString());

		const images = product.thirdStep.images.filter(image => !product.thirdStep.existingImages?.some(existingImage => existingImage.url === image.url));

		for await (const image of images) {
			let blob = await fetch(image.url).then(r => r.blob());

			let file = new File([blob], image.name, { type: blob.type });

			form.append("images", file, file.name);
		}

		return this.instance.put(product.editingProductId, form, { headers: { "Content-Type": "multipart/form-data" } }).catch(error => {
			throw new Error(error?.response?.data?.title ?? "Something went wrong");
		});
	}

	public async getById(id: string | undefined): Promise<AxiosResponse<SolarProduct>> {
		return this.instance.get(id);
	}

	public async getPaged(page: number, pageSize: number): Promise<AxiosResponse<PaginatedList<SolarProduct>>> {
		return this.instance.get("", { params: { page, pageSize } });
	}

	public async archive(id: string): Promise<AxiosResponse<void>> {
		return this.instance.delete(`${id}`);
	}

	public async pause(id: string): Promise<AxiosResponse<void>> {
		return this.instance.post(`pause/${id}`);
	}

	public async getProductsFromCategory(category: string, queryParams: string): Promise<AxiosResponse<any>> {
		return this.instance.get(`category/${category}?${queryParams}`);
	}

	public async getNewPaged(page: number, pageSize: number): Promise<AxiosResponse<PaginatedList<NewSolarProduct>>> {
		return this.instance.get("new", { params: { page, pageSize } }).then(response => {
			return Promise.resolve({
				...response,
				data: {
					...response.data,
					items: response.data.items.map((item: any) => {
						return {
							...item,
							dateAdded: new Date(item.dateAdded),
						};
					}),
				},
			});
		});
	}

	public async getExistingPaged(page: number, pageSize: number): Promise<AxiosResponse<PaginatedList<ExistingSolarProduct>>> {
		return this.instance.get("existing", { params: { page, pageSize } });
	}

	public async addNewLoanProduct(productId: string, loanProduct: LoanProduct): Promise<AxiosResponse<void>> {
		return this.instance.post(`${productId}/loan-product`, loanProduct)
	}

	public async editLoanProduct(
		productId: string,
		loanProduct: LoanProduct,
		actionCode?: string
	): Promise<
		AxiosResponse<{
			otpSent: boolean;
		}>
	> {
		return this.instance.put(`${productId}/loan-product`, {
			...loanProduct,
			actionCode,
		});
	}

	public async adminPause(id: string, actionCode?: string): Promise<AxiosResponse<void>> {
		return this.instance.post(`admin/pause/${id}`, { actionCode });
	}

	public async recommendBestFit({
		powerNeeded,
		monthsToPay,
		monthlyBudget,
	}: {
		powerNeeded: number;
		monthsToPay: number;
		monthlyBudget: number;
	}): Promise<AxiosResponse<SolarProduct[]>> {
		return this.instance
			.get("recommend/best-fit", {
				params: {
					powerNeeded,
					monthsToPay,
					monthlyBudget,
				},
			})
			.then(response => {
				return Promise.resolve({
					...response,
					data: response.data.solarProductsThatFitTheBest,
				});
			});
	}

	public async recommendBudgetFit({
		page,
		pageSize,
		monthsToPay,
		monthlyBudget,
	}: {
		page: number;
		pageSize: number;
		monthsToPay: number;
		monthlyBudget: number;
	}): Promise<AxiosResponse<PaginatedList<SolarProduct>>> {
		return this.instance
			.get("recommend/budget", {
				params: {
					page,
					pageSize,
					monthsToPay,
					monthlyBudget,
				},
			})
			.then(response => {
				return Promise.resolve({
					...response,
					data: response.data.solarProductsThatFitTheBudget,
				});
			});
	}

	public async recommendPowerFit({
		page,
		pageSize,
		powerNeeded,
	}: {
		page: number;
		pageSize: number;
		powerNeeded: number;
	}): Promise<AxiosResponse<PaginatedList<SolarProduct>>> {
		return this.instance
			.get("recommend/power-needs", {
				params: {
					page,
					pageSize,
					powerNeeded,
				},
			})
			.then(response => {
				return Promise.resolve({
					...response,
					data: response.data.solarProductsThatFitThePowerNeeds,
				});
			});
	}

	public async getStoreProductsByCategories(): Promise<AxiosResponse<Array<CategoryWithProduct>>> {
		return this.instance.get("get-product-details-by-categories");
	}

	public async getbyName(search: string): Promise<AxiosResponse<Array<SolarProduct>>> {
		return this.instance.get(`search/${search}`);
	}
}

const solarProductService = new SolarProductService();

export default solarProductService;
